import { purpleGradientTextStyles } from '#app/components/MembersOnlyMarketing/PurpleGradientText.js';
import { Text } from '#app/components/Primitives/Text.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { CloseIcon, IconSvgProps } from '@contra/icons/icons';
import { FC, ReactNode } from 'react';
import { css, type DefaultTheme, styled } from 'styled-components';

export type ProfileBadgeTagVariant =
  | 'black'
  | 'blue'
  | 'blue-purple'
  | 'green'
  | 'indigo'
  | 'lavender'
  | 'pink'
  | 'purple'
  | 'purple-gradient'
  | 'salmon'
  | 'solid-blue'
  | 'solid-green'
  | 'white';

type ProfileBadgeTagProps = {
  readonly ariaLabel?: string;
  readonly Icon: FC<IconSvgProps> | null;
  readonly iconBackgroundColor?: string;
  readonly iconColor?: string;
  readonly label: string;
  readonly secondaryPartner?: {
    readonly icon: ReactNode;
    readonly label: string;
  };
  readonly size?: 'default' | 'small';
  readonly tooltip?: string;
  readonly variant: ProfileBadgeTagVariant;
};

const defaultBorder = css`
  border: 1px solid ${({ theme }) => theme.colorsV2.gray30};
`;
const defaultBackground = css`
  background-color: ${({ theme }) => theme.colorsV2.white};
`;

const getTagColors = (theme: DefaultTheme, variant: ProfileBadgeTagVariant) => {
  switch (variant) {
    case 'black':
      return css`
        &:hover {
          background-color: ${theme.colorsV2.black};
          border-color: ${theme.colorsV2.black};
          color: ${theme.colorsV2.white};
        }
      `;
    case 'blue':
      return css`
        &:hover {
          background-color: ${theme.colorsV2.blue05};
          border-color: ${theme.colorsV2.blue05};
        }
      `;
    case 'blue-purple':
      return css`
        &:hover {
          background-color: #e9ebfd;
          border-color: #e9ebfd;
        }
      `;
    case 'green':
      return css`
        &:hover {
          background-color: ${theme.colorsV2.green05};
          border-color: ${theme.colorsV2.green05};
        }
      `;
    case 'indigo':
      return css`
        &:hover {
          background-color: #e9ebfd;
          border-color: #e9ebfd;
        }
      `;
    case 'lavender':
      return css`
        &:hover {
          background-color: #f4eafb;
          border-color: #f4eafb;
        }
      `;
    case 'pink':
      return css`
        &:hover {
          background-color: #ffe5fe;
          border-color: #ffe5fe;
        }
      `;
    case 'purple':
      return css`
        &:hover {
          background-color: #eeebff;
          border-color: #eeebff;
        }
      `;
    case 'purple-gradient':
      return css`
        > span {
          ${purpleGradientTextStyles}
          background-image: linear-gradient(160.09deg, #222834 1.29%, #6A57E3 67.55%, #CD74DD 93.23%);
        }
      `;
    case 'salmon':
      return css`
        &:hover {
          background-color: #ffe2db;
          border-color: #ffe2db;
        }
      `;
    case 'solid-blue':
      return css`
        background-color: ${theme.colorsV2.blue05};
        border-color: ${theme.colorsV2.blue05};

        &:hover {
          background-color: ${theme.colorsV2.blue10};
          border-color: ${theme.colorsV2.blue10};
        }
      `;
    case 'solid-green':
      return css`
        background-color: ${theme.colorsV2.green05};
        border-color: ${theme.colorsV2.green05};
      `;

    case 'white':
      return css`
        &:hover {
          background-color: ${theme.colorsV2.white};
          color: ${theme.colorsV2.gray60};
          border-color: ${theme.colorsV2.gray30};
        }
      `;

    default:
      throw new Error(`Invalid variant: ${variant}`);
  }
};

const TagContainer = styled.div<{
  $size: ProfileBadgeTagProps['size'];
  $variant: ProfileBadgeTagProps['variant'];
}>`
  ${({ $size, $variant, theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;

    width: fit-content;
    height: fit-content;

    ${$size === 'small'
      ? css`
          gap: 4px;
          padding: 3px 6px 3px 4px;
          min-height: 24px;
        `
      : css`
          gap: 5px;
          padding: 6px;
          min-height: 32px;
        `}

    border-radius: 5px;

    cursor: default;

    ${defaultBorder};
    ${defaultBackground};
    ${getTagColors(theme, $variant)}

    transition: all 0.2s ease-out;
  `}
`;

const TagText = styled(Text)<{
  $size: ProfileBadgeTagProps['size'];
  $variant: ProfileBadgeTagProps['variant'];
}>`
  ${({ $size, $variant, theme }) => css`
    ${$size === 'small'
      ? css`
          font-size: 10px;
        `
      : css`
          font-size: 13px;
        `}

    line-height: 16px;

    ${$size === 'small'
      ? css`
          letter-spacing: 1.2px;
        `
      : css`
          letter-spacing: 0.2px;
        `}

    white-space: nowrap;
    color: ${theme.colorsV2.gray80};

    ${$variant === 'solid-blue'
      ? css`
          color: ${theme.colorsV2.blue60};
        `
      : undefined}
  `}
`;

const BACKGROUND_PADDING = 1;
const ICON_SIZE = 16;

const IconContainer = styled.div<{
  $backgroundColor?: string;
  $color?: string;
}>`
  ${({ $backgroundColor, $color }) => css`
    display: flex;
    justify-content: center;
    align-items: center;

    ${$color
      ? css`
          svg > * > * {
            fill: ${$color};
          }
        `
      : undefined}

    ${$backgroundColor
      ? css`
          background-color: ${$backgroundColor};
          border-radius: 2px;
          padding: ${BACKGROUND_PADDING}px;
        `
      : undefined}
  `}
`;

const StyledCloseIcon = styled(CloseIcon)`
  ${({ theme }) => css`
    color: ${theme.colorsV2.gray50};
    width: 12px;
    height: 12px;
  `}
`;

export const ProfileBadgeTag = ({
  ariaLabel,
  Icon,
  iconBackgroundColor,
  iconColor,
  label,
  secondaryPartner,
  size = 'default',
  tooltip,
  variant,
}: ProfileBadgeTagProps) => {
  const content = (
    <TagContainer
      $size={size}
      $variant={variant}
      aria-label={ariaLabel}
    >
      {Icon ? (
        <IconContainer
          $backgroundColor={iconBackgroundColor}
          $color={iconColor}
        >
          <Icon
            size={
              iconBackgroundColor
                ? ICON_SIZE - BACKGROUND_PADDING * 2
                : ICON_SIZE
            }
          />
        </IconContainer>
      ) : null}
      <TagText
        $size={size}
        $variant={variant}
        as="span"
        textStyle="overlineBold"
      >
        {label}
      </TagText>
      {secondaryPartner ? (
        <>
          <StyledCloseIcon />

          {secondaryPartner.icon}

          <TagText
            $size={size}
            $variant={variant}
            as="span"
            textStyle="overlineBold"
          >
            {secondaryPartner.label}
          </TagText>
        </>
      ) : null}
    </TagContainer>
  );

  if (tooltip) {
    return (
      <Tooltip
        containerWidth="fit-content"
        content={tooltip}
        strategy="absolute"
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};
